var _excluded = ["listedDate"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React, { useEffect, useMemo } from 'react';
import { SearchQuery } from '@domain-group/fe-allhomes-search-api';
import { EventBoundary, useSendEvent } from '@domain-group/fe-co-analytics';
import { useAllhomesApi } from '@domain-group/fe-co-allhomes-api-client';
import { Stack } from '@domain-group/fe-blueprint-ui/lib/js/layout/stack';
import { NoResults } from '../no-results/no-results';
import { SimilarProperties } from './similar-properties';
import * as styles from './cross-promotion.style';
import { useSimilarPropertiesForSearchQuery } from './similar-properties.query.generated';
import { jsx as ___EmotionJSX } from "@emotion/core";
export var CrossPromotion = _ref => {
  var _data$similarListings3, _data$similarListings4;
  var searchQuery = _ref.searchQuery,
    firstSearch = _ref.firstSearch,
    _ref$searchResults = _ref.searchResults,
    searchResults = _ref$searchResults === void 0 ? [] : _ref$searchResults,
    _ref$options = _ref.options,
    _ref$options$similarP = _ref$options.similarProperties,
    serverSimilarProperties = _ref$options$similarP === void 0 ? [] : _ref$options$similarP,
    _ref$options$attempts = _ref$options.attempts,
    attempts = _ref$options$attempts === void 0 ? [] : _ref$options$attempts,
    _ref$options$minResul = _ref$options.minResults,
    minResults = _ref$options$minResul === void 0 ? 5 : _ref$options$minResul,
    initialCrossPromotionQuery = _ref$options.query,
    _ref$options$limit = _ref$options.limit,
    limit = _ref$options$limit === void 0 ? 10 : _ref$options$limit,
    maxDuration = _ref$options.maxDuration,
    propertyAlertProps = _ref.propertyAlertProps,
    _ref$onIncludeSurroun = _ref.onIncludeSurroundingSuburbs,
    onIncludeSurroundingSuburbs = _ref$onIncludeSurroun === void 0 ? () => {} : _ref$onIncludeSurroun;
  var _useAllhomesApi = useAllhomesApi(),
    getLogger = _useAllhomesApi.getLogger;
  var _useSendEvent = useSendEvent(),
    sendEvent = _useSendEvent.sendEvent;
  var logger = getLogger('fe-pa-allhomes-listing-search-results#CrossPromotion');
  var _ref2 = firstSearch && initialCrossPromotionQuery !== null && initialCrossPromotionQuery !== void 0 && initialCrossPromotionQuery.filters ? initialCrossPromotionQuery.filters : searchQuery.toQueryObject().filters,
    _unused = _ref2.listedDate,
    filters = _objectWithoutProperties(_ref2, _excluded);
  var similarProperties = firstSearch ? serverSimilarProperties : [];
  var serverAttempts = useMemo(() => firstSearch ? attempts : [], [firstSearch, attempts]);
  var resultCount = searchResults.length;
  var crossPromotionRequired = resultCount < minResults && similarProperties.length < minResults;
  var excludeListingIds = searchResults.slice(0, minResults).map(result => "".concat(result.listing.id));
  var canExpandSearch = searchQuery.getLocalities().some(locality => ['STREET', 'DIVISION', 'DISTRICT'].includes(locality.type));
  logger.debug('rendering CrossPromotion component', {
    firstSearch,
    resultCount,
    similarProperties,
    serverAttempts,
    crossPromotionRequired
  });
  var _useSimilarProperties = useSimilarPropertiesForSearchQuery({
      variables: {
        filters,
        listingType: searchQuery.listingType,
        options: {
          limit,
          minResults,
          maxDuration,
          skipAttempts: serverAttempts.length,
          excludeListingIds
        }
      },
      skip: !crossPromotionRequired,
      ssr: false
    }),
    data = _useSimilarProperties.data,
    loading = _useSimilarProperties.loading;
  useEffect(() => {
    logger.debug('query state changed', {
      loading,
      data
    });
    if (!loading && (data || firstSearch && serverSimilarProperties.length > 0)) {
      var _data$similarListings, _data$similarListings2;
      logger.debug('Not loading, so send event');
      var _ref3 = (_data$similarListings = data === null || data === void 0 ? void 0 : data.similarListingsForSearch) !== null && _data$similarListings !== void 0 ? _data$similarListings : {},
        totalAttempts = _ref3.totalAttempts,
        totalDuration = _ref3.totalDuration,
        totalMatched = _ref3.totalMatched,
        _ref3$attempts = _ref3.attempts,
        clientAttempts = _ref3$attempts === void 0 ? [] : _ref3$attempts;
      var totalServerDuration = serverAttempts.reduce((sum, attempt) => sum + (attempt.duration || 0), 0);
      var queryObject = data === null || data === void 0 ? void 0 : (_data$similarListings2 = data.similarListingsForSearch) === null || _data$similarListings2 === void 0 ? void 0 : _data$similarListings2.queryObject;
      var finalFilters = queryObject && SearchQuery.fromPossibleQueryObject(searchQuery.listingType, JSON.parse(queryObject)).filters.toUrlParameters();
      sendEvent('similar-properties-loaded', {
        initialQuery: searchQuery.filters.toUrlParameters(),
        finalQuery: finalFilters,
        serverDuration: firstSearch ? totalServerDuration : undefined,
        totalAttempts: totalAttempts !== null && totalAttempts !== void 0 ? totalAttempts : serverAttempts.length + clientAttempts.length,
        clientDuration: totalDuration,
        totalMatched: totalMatched || (serverSimilarProperties === null || serverSimilarProperties === void 0 ? void 0 : serverSimilarProperties.length)
      });
    }
  }, [loading, data, sendEvent, serverSimilarProperties === null || serverSimilarProperties === void 0 ? void 0 : serverSimilarProperties.length, searchQuery.listingType, searchQuery.filters, firstSearch, logger, serverAttempts]);
  var properties = (_data$similarListings3 = data === null || data === void 0 ? void 0 : (_data$similarListings4 = data.similarListingsForSearch) === null || _data$similarListings4 === void 0 ? void 0 : _data$similarListings4.similarProperties) !== null && _data$similarListings3 !== void 0 ? _data$similarListings3 : similarProperties;
  var fewResults = resultCount < minResults;
  return fewResults && ___EmotionJSX(EventBoundary, {
    data: {
      pageSection: 'similar-properties'
    }
  }, ___EmotionJSX(Stack, {
    vertical: true,
    gap: "xl",
    css: styles.crossPromotion
  }, ___EmotionJSX(NoResults, {
    resultCount: resultCount,
    propertyAlertProps: propertyAlertProps,
    onIncludeSurroundingSuburbs: onIncludeSurroundingSuburbs,
    canExpandSearch: canExpandSearch
  }), (properties === null || properties === void 0 ? void 0 : properties.length) > 0 && ___EmotionJSX(SimilarProperties, {
    properties: properties,
    loading: loading
  })));
};